import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit2, Trash2, X, Check, Search, Upload, Star, RefreshCw, Film, EyeOff, Award } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SERVICE_CATEGORIES } from "@/portfolioServices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Show Form Modal ──
const ShowFormModal = ({ show, onSave, onClose, token }) => {
  const [form, setForm] = useState(show || {
    title: "", year: "", type: "Series", client: "",
    languages: [], services: [], description: "", poster_url: "", is_featured: false, is_sample: false
  });
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const lookupTitle = async () => {
    if (!form.title) return;
    setLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/portfolio/lookup`, { params: { title: form.title } });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
        poster_url: data.poster_url || f.poster_url,
      }));
      if (data.found && data.poster_url) toast.success("Poster found!");
      else if (data.found) toast.info("Found, but no poster — try custom upload.");
      else toast.warning("Not found on Wikipedia. Upload manually.");
    } catch {
      toast.error("Lookup failed");
    } finally {
      setLookingUp(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, poster_url: fullUrl }));
      toast.success("Poster uploaded!");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleService = (svc) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(svc) ? f.services.filter((s) => s !== svc) : [...f.services, svc],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, languages: typeof form.languages === "string" ? form.languages.split(",").map((s) => s.trim()).filter(Boolean) : form.languages };
      if (show) {
        await axios.put(`${API}/admin/portfolio/${show.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/portfolio`, payload, { headers });
      }
      toast.success(show ? "Show updated" : "Show added");
      onSave();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const langString = Array.isArray(form.languages) ? form.languages.join(", ") : form.languages;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#1f1f23] sticky top-0 bg-[#0B0B0D] z-10">
          <h3 className="text-xl">{show ? "Edit Show" : "Add New Show"}</h3>
          <button onClick={onClose} className="text-[#52525B] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Title + auto-fetch */}
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Title *</label>
            <div className="flex gap-2">
              <input data-testid="show-title-input" className="glass-input flex-1" required
                placeholder="e.g. Squid Game"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <button type="button" onClick={lookupTitle} disabled={lookingUp || !form.title}
                className="secondary-button text-xs py-2 px-4 flex items-center gap-2 shrink-0">
                {lookingUp ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Auto-fetch
              </button>
            </div>
            <p className="mono-font text-[10px] text-[#52525B] mt-1">Pulls title, description & poster from Wikipedia</p>
          </div>

          {/* Poster */}
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Poster</label>
            {form.poster_url ? (
              <div className="flex gap-3 items-start">
                <img src={form.poster_url} alt="poster" className="w-24 h-36 object-cover rounded-lg border border-[#1f1f23]" />
                <div className="flex-1 space-y-2">
                  <input className="glass-input text-xs" placeholder="Poster URL"
                    value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => fileRef.current?.click()} className="secondary-button text-xs py-1.5 px-3 flex items-center gap-1.5">
                      <Upload className="w-3 h-3" /> {uploading ? "Uploading..." : "Replace"}
                    </button>
                    <button type="button" onClick={() => setForm({ ...form, poster_url: "" })}
                      className="text-xs text-[#52525B] hover:text-red-400 px-2">Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <input className="glass-input text-xs" placeholder="Paste poster URL or upload"
                  value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="secondary-button text-xs py-2 px-3 flex items-center gap-1.5 shrink-0">
                  <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
                </button>
              </div>
            )}
            <input ref={fileRef} type="file" className="hidden" accept="image/*" onChange={handleUpload} />
          </div>

          {/* Year + Type + Client */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Year</label>
              <input className="glass-input" placeholder="2024" value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
            <div>
              <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Type</label>
              <select className="glass-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option>Series</option>
                <option>Film</option>
                <option>Documentary</option>
                <option>Short</option>
              </select>
            </div>
            <div>
              <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Client</label>
              <input className="glass-input" placeholder="Netflix" value={form.client || ""}
                onChange={(e) => setForm({ ...form, client: e.target.value })} />
            </div>
          </div>

          {/* Languages */}
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Languages (comma-separated)</label>
            <input className="glass-input" placeholder="English, Hindi, Spanish, French"
              value={langString} onChange={(e) => setForm({ ...form, languages: e.target.value })} />
          </div>

          {/* Description */}
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Description</label>
            <textarea className="glass-textarea" rows={3} placeholder="Short synopsis or our role"
              value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          {/* Services Multi-select */}
          <div>
            <label className="mono-font text-[10px] text-[#17B352] mb-2 block uppercase tracking-wider">
              Services Delivered ({form.services.length} selected)
            </label>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2 border border-[#1f1f23] rounded-lg p-3">
              {SERVICE_CATEGORIES.map((cat) => (
                <div key={cat.category}>
                  <p className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider mb-1.5">{cat.category}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.services.map((svc) => {
                      const active = form.services.includes(svc);
                      return (
                        <button key={svc} type="button" onClick={() => toggleService(svc)}
                          className={`mono-font text-[10px] px-2.5 py-1 rounded-full border transition-all ${
                            active
                              ? "border-[#17B352] text-[#17B352] bg-[rgba(23,179,82,0.1)]"
                              : "border-[#1f1f23] text-[#A1A1AA] hover:border-[#52525B]"
                          }`}>
                          {svc}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Featured */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                className="w-4 h-4 accent-[#17B352]" />
              <span className="text-sm text-[#A1A1AA]">Featured</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_sample}
                onChange={(e) => setForm({ ...form, is_sample: e.target.checked })}
                className="w-4 h-4 accent-[#52525B]" />
              <span className="text-sm text-[#A1A1AA]">Mark as sample (hidden unless toggled on)</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-[#1f1f23] sticky bottom-0 bg-[#0B0B0D] pb-1">
            <button type="submit" disabled={saving}
              className="glow-button flex items-center gap-2 disabled:opacity-50">
              {saving ? "Saving..." : <><Check className="w-4 h-4" /> {show ? "Update" : "Add Show"}</>}
            </button>
            <button type="button" onClick={onClose} className="secondary-button text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ── Portfolio Admin Panel ──
const PortfolioAdminPanel = ({ token }) => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSamples, setShowSamples] = useState(false);
  const [display, setDisplay] = useState({ show_no_poster: false, show_client_logos: true, show_on_homepage: true });
  const [viewFilter, setViewFilter] = useState("real");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, settingsRes, displayRes] = await Promise.all([
        axios.get(`${API}/admin/portfolio/all`, { headers }),
        axios.get(`${API}/admin/portfolio/settings`, { headers }),
        axios.get(`${API}/portfolio/display-settings`),
      ]);
      setShows(allRes.data);
      setShowSamples(settingsRes.data.show_samples);
      setDisplay(displayRes.data);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSamples = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/portfolio/settings`,
        { show_samples: !showSamples }, { headers });
      setShowSamples(data.show_samples);
      toast.success(data.show_samples ? "Samples now visible on website" : "Samples hidden from website");
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const toggleDisplay = async (key) => {
    try {
      const { data } = await axios.put(`${API}/admin/portfolio/display-settings`,
        { [key]: !display[key] }, { headers });
      setDisplay(data);
      const label = key === "show_no_poster" ? "Titles without posters"
        : key === "show_client_logos" ? "Client logos strip"
        : "Portfolio link on homepage";
      toast.success(`${label} ${data[key] ? "shown" : "hidden"}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const resyncCatalog = async () => {
    if (!window.confirm("Re-sync the bundled catalog (655 entries)? Existing titles are kept; only missing ones are inserted.")) return;
    try {
      const { data } = await axios.post(`${API}/admin/portfolio/seed-bundle`,
        { force: true }, { headers });
      if (data?.status === "ok") {
        toast.success(`Re-sync complete · inserted=${data.inserted} · skipped=${data.skipped}`);
        fetchAll();
      } else {
        toast.error(`Re-sync ${data?.status}: ${data?.reason || "no change"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-sync failed");
    }
  };

  const deleteShow = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`, { headers });
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const realShows = shows.filter((s) => !s.is_sample);
  const sampleShows = shows.filter((s) => s.is_sample);
  const filtered = viewFilter === "all" ? shows : viewFilter === "samples" ? sampleShows : realShows;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h3 className="text-xl">Portfolio Shows</h3>
          <p className="mono-font text-[10px] text-[#52525B] mt-1 uppercase tracking-wider">
            {realShows.length} real · {sampleShows.length} sample
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="resync-catalog-btn"
            onClick={resyncCatalog}
            title="Re-sync bundled catalog (idempotent · inserts missing only)"
            className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352] transition-colors inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Re-sync Catalog
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="glow-button flex items-center gap-2 text-sm py-2 px-5">
            <Plus className="w-4 h-4" /> Add Show
          </button>
        </div>
      </div>

      {/* Samples Toggle Bar */}
      <div className="glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Film className="w-5 h-5 text-[#17B352] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">Sample shows on public Portfolio</p>
            <p className="mono-font text-[10px] text-[#52525B] mt-0.5 uppercase tracking-wider">
              {showSamples ? "Currently visible on graphikpro.com/portfolio" : "Hidden — only real projects shown"}
            </p>
          </div>
        </div>
        <button onClick={toggleSamples}
          className={`mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-all flex items-center gap-2 ${
            showSamples
              ? "border-[#32D74B] text-[#32D74B] bg-[rgba(50,215,75,0.08)]"
              : "border-[#52525B] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352]"
          }`}>
          <div className={`w-2 h-2 rounded-full ${showSamples ? "bg-[#32D74B]" : "bg-[#52525B]"}`} />
          {showSamples ? "Samples ON" : "Samples OFF"}
        </button>
      </div>

      {/* No-poster titles toggle */}
      <div className="glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <EyeOff className="w-5 h-5 text-[#17B352] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">Titles without posters</p>
            <p className="mono-font text-[10px] text-[#52525B] mt-0.5 uppercase tracking-wider">
              {display.show_no_poster
                ? "Visible as a text-only marquee on /portfolio"
                : "Hidden — only items with poster artwork are shown"}
            </p>
          </div>
        </div>
        <button data-testid="toggle-no-poster" onClick={() => toggleDisplay("show_no_poster")}
          className={`mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-all flex items-center gap-2 ${
            display.show_no_poster
              ? "border-[#32D74B] text-[#32D74B] bg-[rgba(50,215,75,0.08)]"
              : "border-[#52525B] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352]"
          }`}>
          <div className={`w-2 h-2 rounded-full ${display.show_no_poster ? "bg-[#32D74B]" : "bg-[#52525B]"}`} />
          {display.show_no_poster ? "Showing" : "Hidden"}
        </button>
      </div>

      {/* Client logos toggle */}
      <div className="glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Award className="w-5 h-5 text-[#17B352] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">Client logo strip (grayscale)</p>
            <p className="mono-font text-[10px] text-[#52525B] mt-0.5 uppercase tracking-wider">
              Iyuno · Deluxe · Zoo · Facebook · Disney+ Hotstar · Agaro · Disano
            </p>
          </div>
        </div>
        <button data-testid="toggle-client-logos" onClick={() => toggleDisplay("show_client_logos")}
          className={`mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-all flex items-center gap-2 ${
            display.show_client_logos
              ? "border-[#32D74B] text-[#32D74B] bg-[rgba(50,215,75,0.08)]"
              : "border-[#52525B] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352]"
          }`}>
          <div className={`w-2 h-2 rounded-full ${display.show_client_logos ? "bg-[#32D74B]" : "bg-[#52525B]"}`} />
          {display.show_client_logos ? "Showing" : "Hidden"}
        </button>
      </div>

      {/* Show Portfolio on Homepage toggle */}
      <div className="glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Film className="w-5 h-5 text-[#17B352] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">Portfolio link on homepage</p>
            <p className="mono-font text-[10px] text-[#52525B] mt-0.5 uppercase tracking-wider">
              {display.show_on_homepage
                ? "Footer 'Portfolio' link visible · /portfolio URL always active"
                : "Hidden from footer · /portfolio URL still accessible directly"}
            </p>
          </div>
        </div>
        <button data-testid="toggle-portfolio-homepage" onClick={() => toggleDisplay("show_on_homepage")}
          className={`mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-all flex items-center gap-2 ${
            display.show_on_homepage
              ? "border-[#32D74B] text-[#32D74B] bg-[rgba(50,215,75,0.08)]"
              : "border-[#52525B] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352]"
          }`}>
          <div className={`w-2 h-2 rounded-full ${display.show_on_homepage ? "bg-[#32D74B]" : "bg-[#52525B]"}`} />
          {display.show_on_homepage ? "Visible" : "Hidden"}
        </button>
      </div>

      {/* View filter */}
      <div className="flex gap-2">
        {[
          { id: "real", label: `Real (${realShows.length})` },
          { id: "samples", label: `Samples (${sampleShows.length})` },
          { id: "all", label: `All (${shows.length})` },
        ].map((f) => (
          <button key={f.id} onClick={() => setViewFilter(f.id)}
            className={`mono-font text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-all ${
              viewFilter === f.id
                ? "border-[#17B352] text-[#17B352] bg-[rgba(23,179,82,0.1)]"
                : "border-[#1f1f23] text-[#52525B] hover:border-[#52525B]"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 mono-font text-sm text-[#52525B]">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center">
          <p className="text-[#52525B] mono-font text-sm">No shows in this view.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((s) => (
            <div key={s.id} className="glass-card rounded-lg overflow-hidden">
              <div className="relative aspect-[2/3] bg-[#1a1a1c]">
                {s.poster_url ? (
                  <img src={s.poster_url} alt={s.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full p-2 text-center italic text-[#52525B] text-sm" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    {s.title}
                  </div>
                )}
                {s.is_featured && (
                  <div className="absolute top-1.5 left-1.5 bg-[#17B352] text-black p-1 rounded">
                    <Star className="w-3 h-3 fill-current" />
                  </div>
                )}
                {s.is_sample && (
                  <div className="absolute top-1.5 right-1.5 bg-[#52525B] text-white mono-font text-[8px] px-1.5 py-0.5 rounded uppercase tracking-wider">
                    Sample
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium line-clamp-1">{s.title}</p>
                <p className="mono-font text-[10px] text-[#52525B] mt-0.5 truncate">
                  {s.client || s.year || "—"}
                </p>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => { setEditing(s); setShowForm(true); }}
                    className="flex-1 p-1.5 rounded hover:bg-[rgba(255,255,255,0.05)] text-[#A1A1AA] hover:text-[#17B352] transition-colors flex justify-center">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteShow(s.id)}
                    className="flex-1 p-1.5 rounded hover:bg-[rgba(255,255,255,0.05)] text-[#A1A1AA] hover:text-[#FF375F] transition-colors flex justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <ShowFormModal show={editing} token={token}
            onClose={() => setShowForm(false)}
            onSave={() => { setShowForm(false); fetchAll(); }} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PortfolioAdminPanel;
